import { Inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from "@angular/router";
import { L10N_LOCALE, L10nLocale } from "angular-l10n";
import { forkJoin, Observable } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { BaseService } from "src/app/core/services/base.service";
import { ClinicHistoryService } from "src/app/core/services/clinic-history.service";
import { DoctorService } from "src/app/core/services/doctors.service";
import { PublicProfileService } from "src/app/core/services/public-profile.service";
import { ClinicHistoryHashed } from "src/app/shared/models/clinic-history/clinic-history-hashed.model";
import { LicenseDoctor } from "src/app/shared/models/clinic-history/license-doctor.model";
import { LicenseDoctorInfo } from "src/app/shared/models/people/license-doctor-info.mode";
import { LicenseDoctorItem } from "src/app/shared/models/people/lincese-doctor-item.model";
import { PublicProfile } from "src/app/shared/models/people/public-profile.model";

@Injectable({
    providedIn: 'root'
})
export class ClinicHistoryByHashResolver implements Resolve<ClinicHistoryByHashResult> {
    
    constructor(
        @Inject(L10N_LOCALE) public locale: L10nLocale,
        private clinicHistoryServices: ClinicHistoryService,
        private doctorService: DoctorService,
        private publicProfileService: PublicProfileService,
        private router: Router
    ) { }

    resolve(route: ActivatedRouteSnapshot, _: RouterStateSnapshot): ClinicHistoryByHashResult | Observable<ClinicHistoryByHashResult> {
        const currentNavigation = this.router.getCurrentNavigation();
        const idHistoriaClinica = route.queryParams['id'] || route.params['id'];
        return currentNavigation?.extras?.state as ClinicHistoryByHashResult || this.getClinicHistoryByHash(idHistoriaClinica);
    }

    private getClinicHistoryByHash(idHistoriaClinica: string,): Observable<ClinicHistoryByHashResult> {

        idHistoriaClinica = decodeURIComponent(idHistoriaClinica);

        return this.clinicHistoryServices.getClinicHistoryByHash(idHistoriaClinica)
            .pipe(take(1))
            .pipe(
                switchMap(this.getDoctorLicense.bind(this))
            );
    }

    private getDoctorLicense(clinicHistory: ClinicHistoryHashed) {
        const lincese$ = this.doctorService.getLicense(clinicHistory.idResponsableAtencion);
        const profile$ = this.publicProfileService.getPublicProfileByIds([clinicHistory.idResponsableAtencion]);
        return forkJoin([lincese$, profile$]).pipe(map(([license, profile]) => this.mapFinalResponse(clinicHistory, license, profile[0])));
    }

    private mapFinalResponse(clinicHistory: ClinicHistoryHashed, arg1: LicenseDoctor, arg2: PublicProfile | null): ClinicHistoryByHashResult {
        const informacionResponsableServicio = arg1.informacionResponsableServicio;
        const firma = arg1.documentosFirma.filter(x => x.idDocumento == informacionResponsableServicio.idDocumentoImagenFirma).map(x => x.direccionUbicacionWeb)[0];
        const licencia = arg1.licenciasProfesional.filter(x => x.idIdentificacionPersona == informacionResponsableServicio.idIdentificacionIdoneidadProfesionalDefault)[0];

        informacionResponsableServicio.direccionUbicacionFotoMedico = informacionResponsableServicio.direccionUbicacionFotoMedico || arg2?.ubicacionFoto || '';
        informacionResponsableServicio.nombreIdentificacionPerfil = informacionResponsableServicio.nombreIdentificacionPerfil || arg2?.username || '';
        informacionResponsableServicio.nombreResponsableServicio = informacionResponsableServicio.nombreResponsableServicio || arg2?.nombrePersona || '';

        return { avatarModel: informacionResponsableServicio, firma, licencia, ...clinicHistory };
    }

}

export interface ClinicHistoryByHashResult extends ClinicHistoryHashed {
    avatarModel: LicenseDoctorInfo;
    firma: string;
    licencia: LicenseDoctorItem;
}
